import * as React from 'react';
import type { EntityDetail } from '@web-config-app/core';
import type { EntityReferenceControlProps } from '@web-config-app/entity-form';
import {
  useAnnotatedSchemaProps,
  useReferencedByDomainEntityIdData,
  useEntityDetailsProps,
  getComposedSchemaPropertyPath,
  formatControlStatus,
} from '@web-config-app/entity-form';
import { useEntityReferenceControlState } from '../internals/use-entity-reference-control-state';
import { EntitySelectControlPresenter } from '../internals/entity-select-control-presenter.component';
import { useEntityReferenceControlContext } from '../internals/use-entity-reference-control-context';
/**
 * Renders for schemas that have a defined `x-entity-reference.domainEntityType` value. This control can operate on
 * entity references that are pure domainEntityIds
 */

export const ConfigEntityReferenceControlWrapper: React.FC<
  EntityReferenceControlProps
> = ({
  schema,
  handleChange,
  path,
  data: domainEntityId,
  referencedEntity,
  enabled,
  domainEntityAttributesQuery,
  errors,
}) => {
  const isArrayItem = useEntityReferenceControlContext();
  const { formPath, currentAuthoringEnvironment } = useEntityDetailsProps();

  const { label, hint, banner } = useAnnotatedSchemaProps(
    schema,
    null,
    getComposedSchemaPropertyPath(formPath, path),
  );
  const { statusMessage, inputStatus } = formatControlStatus({
    errors,
    label,
  });
  /**
   * `useReferencedEntityData` allows us to retrieve the name and status of the entity to display
   * in the control.
   */

  const {
    referencedEntity: entityReferenceData,
    isLoading,
    isError,
  } = useReferencedByDomainEntityIdData({
    domainEntity: referencedEntity,
    domainEntityId,
    environment: currentAuthoringEnvironment,
  });

  const {
    closeChooseFromList,
    openChooseFromList,
    isAddingFromList,
    addItemTriggerList,
  } = useEntityReferenceControlState({ domainEntity: referencedEntity });

  const onSubmitChooseFromList = (entityDetail: EntityDetail) => {
    handleChange(path, entityDetail.attributes.entityMetadata.domainEntityId);
    closeChooseFromList();
  };

  const onClearEntityReference = () => {
    handleChange(path, undefined);
  };

  return (
    <EntitySelectControlPresenter
      hint={hint}
      enabled={enabled}
      label={label!}
      statusMessage={statusMessage}
      inputStatus={inputStatus}
      banner={banner}
      entityReferenceData={entityReferenceData}
      isLoading={isLoading}
      isError={isError}
      onSubmitChooseFromList={onSubmitChooseFromList}
      addItemTriggerList={addItemTriggerList}
      isAddingFromList={isAddingFromList}
      closeChooseFromList={closeChooseFromList}
      domainEntity={referencedEntity}
      openChooseFromList={openChooseFromList}
      isArrayItem={isArrayItem}
      onClearEntityReference={onClearEntityReference}
      domainEntityAttributesQuery={domainEntityAttributesQuery}
      isRelationshipReference={false}
    />
  );
};
